/**
 * Selected insurer resolver
 *
 * Joins the patient's stored insurance id on `signedUpPatient` with the catalog
 * from `useInsuranceCatalog` for explain and checkout screens.
 */

import { useMemo } from 'react';
import type { Insurance } from '@physiofit-digital/physiofit_types';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';
import { useInsuranceCatalog } from './useInsuranceCatalog';

/**
 * Resolves the full Insurance row for the patient's current selection.
 *
 * @return {Object} `insurance`, `insuranceId`, catalog `loading`, and `error`.
 */
export function useSelectedInsurance(): {
  insurance: Insurance | null;
  insuranceId: string | null;
  loading: boolean;
  error: string | null;
} {
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const insuranceId = signedUpPatient?.webAppOnboarding?.insuranceInfo?.id?.trim() ?? null;
  const { insurances, loading, error } = useInsuranceCatalog();

  const insurance = useMemo(() => {
    if (!insuranceId) {
      return null;
    }
    return insurances.find((row) => row.id === insuranceId) ?? null;
  }, [insurances, insuranceId]);

  return { insurance, insuranceId, loading, error };
}
